import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';

// BE DTO shapes (only fields consumed by the FE)
export interface BeAgencyResponse {
  id?: string;
  name: string;
  vat?: string;
  mainCurrency?: string;
  headAgencyFlag?: boolean;
  headAgency?: string;
  email?: string;
  phoneCode?: string;
  phoneNumber?: string;
  address?: { city?: string; country?: string };
  balances?: BeBalanceResponse[];
  /** Agency logo as a bare base64 payload (no `data:` prefix) plus its original filename.
   *  be-agency-service stores it on the agency row and be-notification-service already
   *  renders it in the hotel booking-confirmation email
   *  (templates/hotel/hotel.html -> `data:{mime};base64,{agency.agencyLogoBase64}`),
   *  falling back to the Hilbertech monogram when unset. There is no `logoUrl` anywhere
   *  in the platform — base64-in-JSON is the only logo transport. */
  agencyLogoBase64?: string;
  logoName?: string;
}

export interface BeBalanceResponse {
  id?: string;
  amount?: number;
  currency?: { abbreviation?: string };
}

export interface BeAgentResponse {
  name?: string;
  surname?: string;
  email: string;
  role?: string;
}

export interface BeTransactionResponse {
  transactionId?: string;
  orderNumber?: string;
  creationTime?: string;
  timeStamp?: string;
  type?: string;
  status?: string;
  amount?: number;
  currency?: string;
  description?: string;
  chargeType?: string;
  balanceId?: string;
  createdBy?: string;
}

// POST /agencies/{id}/charge-balance's response shape (ChargeTransactionResponse) — distinct
// field names from BeTransactionResponse above (`id` not `transactionId`, `timeStamp` only, no
// `creationTime`/`description`/`balanceId`/`createdBy`), verified against the actual decompiled
// jar (be-agency-service compiles this endpoint against a legacy `common:4.0.16` artifact, not
// the be-common-lib repo's same-named class).
export interface BeChargeTransactionResponse {
  id?: string;
  reservationId?: string;
  timeStamp?: string;
  amount?: number;
  currency?: string;
  status?: string;
  chargeType?: string;
  hppUrl?: string;
  resultUrl?: string;
}

export interface BePaginatedTx {
  data?: BeTransactionResponse[];
  totalRecords?: number;
  statistics?: {
    totalTransactions?: number;
    totalAmountPerCurrency?: Record<string, number>;
  };
}

export interface BeMarginResponse {
  id?: string;
  type?: string;
  scopeType?: string;
  value?: number;
  percentage?: number;
  currency?: string;
  connectorId?: string;
  airlineIataCode?: string;
  defaultMargin?: boolean;
}

/** GET /agencies/{id}/currency-margins/effective row. `marketRateUnavailable: true` (with a null
 *  effectiveRatePerEur) is the feed-down state and must stay distinguishable from `percentage: 0`,
 *  which is a real, deliberate "pure market rate" margin. */
export interface BeCurrencyMarginResponse {
  currencyAbbreviation?: string;
  percentage?: number;
  autoUpdateEnabled?: boolean;
  pinnedRatePerEur?: number | null;
  pinnedAt?: string | null;
  liveMarketRatePerEur?: number | null;
  marketRateUnavailable?: boolean;
  effectiveRatePerEur?: number | null;
  sourceMarginId?: string | null;
}

/** GET /currencies/catalog row — the whole platform currency catalogue, derived from
 *  be-agency-service's `Currency` enum. Not agency-scoped: it is the same list for everyone, and
 *  what a given network already quotes is subtracted client-side against the effective rows. */
export interface BeCurrencyCatalogResponse {
  abbreviation?: string;
  name?: string;
  symbol?: string;
  fractionDigits?: number;
  feedAvailable?: boolean;
  /** Live EUR-anchored feed rate. See CurrencyCatalogEntry.liveMarketRatePerEur — the Add
   *  Currency dialog needs it to preview a rate for a currency that has no margin row yet. */
  liveMarketRatePerEur?: number | null;
}

export interface BeCurrencyMarginHistoryResponse {
  id?: string;
  currencyAbbreviation?: string;
  kind?: string;                 // 'MARGIN' | 'RATE'
  oldPercentage?: number | null;
  newPercentage?: number | null;
  oldRatePerEur?: number | null;
  newRatePerEur?: number | null;
  changedByEmail?: string;
  changedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class AgencyApiService {
  private http = inject(HttpClient);
  private base = environment.apiAgencyUrl;

  getMyAgency(): Observable<BeAgencyResponse> {
    return this.http.get<BeAgencyResponse>(`${this.base}/agencies/data`);
  }

  getSubAgencies(): Observable<BeAgencyResponse[]> {
    return this.http.get<{ data?: BeAgencyResponse[] }>(
      `${this.base}/agencies/related-agencies?pageNumber=1&pageSize=100`
    ).pipe(map(r => r.data ?? []));
  }

  createSubAgency(body: object): Observable<BeAgencyResponse> {
    return this.http.post<BeAgencyResponse>(`${this.base}/agencies`, body);
  }

  updateSubAgency(agencyId: string, body: object): Observable<BeAgencyResponse> {
    // be-agency-service's AgencyResource only exposes @PUT /{id} (no PATCH) — APIM only
    // has a PUT operation registered for this path, so a PATCH request 404s at the gateway.
    return this.http.put<BeAgencyResponse>(`${this.base}/agencies/${agencyId}`, body);
  }

  getBalances(): Observable<BeBalanceResponse[]> {
    return this.http.get<BeBalanceResponse[]>(`${this.base}/balances`);
  }

  /** Same data as getBalances(), but for an arbitrary agency (e.g. a sub-agency) rather
   *  than the one resolved from the caller's own JWT — powers the Agencies page's
   *  per-sub-agency "Wallet" drill-in. */
  getBalancesByAgencyId(agencyId: string): Observable<BeBalanceResponse[]> {
    return this.http.get<BeBalanceResponse[]>(`${this.base}/agencies/${agencyId}/balances`);
  }

  addBalance(agencyId: string, currencyAbbreviation: string): Observable<BeBalanceResponse> {
    return this.http.post<BeBalanceResponse>(`${this.base}/agencies/${agencyId}/balances`, { currencyAbbreviation });
  }

  // Wallet top-up ledger (#25). WalletTopUpResource (dedicated request/approve/reject
  // endpoints) was never registered on APIM, so top-ups are implemented as ordinary CHARGE
  // transactions on the generic /transactions API instead (already registered): a PENDING
  // charge doesn't move the balance (see be-agency-service's calculateBalanceTransactions —
  // only COMPLETED counts), approving flips it to COMPLETED and then explicitly refreshes
  // the balance (the status PATCH alone has no balance side effect).
  listTopUps(agencyId: string, currency?: string): Observable<BeTransactionResponse[]> {
    return this.getTransactions(agencyId, 1, 100, currency).pipe(
      map(r => (r.data ?? []).filter(t => t.type === 'CHARGE' && t.chargeType !== 'PLAFOND')),
    );
  }

  requestTopUp(agencyId: string, currency: string, amount: number, description: string): Observable<BeTransactionResponse> {
    return this.http.post<BeTransactionResponse>(`${this.base}/transactions`, {
      agencyId, currency, amount, type: 'CHARGE', chargeType: 'BANK_TRANSFER', status: 'PENDING', description,
    });
  }

  /** Real Credit Card top-up — the same Hosted Payment Page (Revolut) flow fe-flight-fusion-web
   *  already uses via ChargeRequest{amount,currency,type,createdAt,resultUrl} (`status` is
   *  omitted: the BE always forces PENDING for CREDIT_CARD regardless of what's sent, and
   *  flight-fusion's own request omits it too). `createdAt` must be a bare "YYYY-MM-DD" — the BE
   *  field is a Java LocalDate, not a full ISO datetime. Response has different field names than
   *  BeTransactionResponse (`id` not `transactionId`, `timeStamp` only) — see
   *  BeChargeTransactionResponse. Caller opens `hppUrl` in a new tab to complete the payment. */
  chargeBalanceCard(agencyId: string, currency: string, amount: number, resultUrl: string): Observable<BeChargeTransactionResponse> {
    return this.http.post<BeChargeTransactionResponse>(`${this.base}/agencies/${agencyId}/charge-balance`, {
      amount, currency, type: 'CREDIT_CARD', createdAt: new Date().toISOString().slice(0, 10), resultUrl,
    });
  }

  /** Polls the payment gateway for a PENDING transaction (Credit Card top-ups only — there's no
   *  webhook, so the FE settles PENDING -> COMPLETED/FAILED on demand: once on wallet page load
   *  and once when the window regains focus after the user returns from the hosted payment page).
   *  Only updates the transaction's status — the balance was already moved when the charge was
   *  created (see be-agency-service's createChargingTransaction, which credits immediately and
   *  unconditionally; this call never touches the balance). */
  getTransactionStatus(transactionId: string): Observable<{ status?: string }> {
    return this.http.get<{ status?: string }>(`${this.base}/transactions/${transactionId}/status`);
  }

  /** Approve/reject both go through PUT /transactions/{id} (updateExistingTransaction), NOT
   *  the PATCH-only status endpoint: that one (updateTransactionStatus) has no @Transactional
   *  boundary in be-agency-service, so the status change never actually commits — verified live
   *  (200 response echoes the new status, but a fresh GET right after still shows the old one).
   *  PUT is properly @Transactional and also calls refreshBalance() itself, so approving here
   *  correctly moves the balance in one round-trip. */
  private putTransactionStatus(
    t: Pick<BeTransactionResponse, 'transactionId' | 'currency' | 'amount' | 'description'>,
    agencyId: string, status: 'COMPLETED' | 'FAILED', noteSuffix?: string,
  ): Observable<BeTransactionResponse> {
    // NOTE (verified live): updateExistingTransaction never actually assigns `description` from
    // the request onto the entity — sending noteSuffix here is forward-compatible but currently
    // a no-op server-side. A rejection note is NOT durably persisted; the FE only shows it
    // locally until the next reload (see wallet.service.ts / rejectTopUp).
    return this.http.put<BeTransactionResponse>(`${this.base}/transactions/${t.transactionId}`, {
      transactionId: t.transactionId, agencyId, currency: t.currency, amount: t.amount,
      type: 'CHARGE', chargeType: 'BANK_TRANSFER', status,
      description: noteSuffix ? `${t.description ?? ''} — ${noteSuffix}`.trim() : t.description,
    });
  }

  /** Back-office correction of an existing ledger row (#4 of the hotel-agency-management
   *  handoff): amount, status and creation date.
   *
   *  Same PUT as putTransactionStatus() above and for the same reason — the PATCH-only status
   *  endpoint has no @Transactional boundary server-side, so it never commits. PUT maps to
   *  updateExistingTransaction, which does assign amount, status AND timeStamp, and refreshes
   *  the balance afterwards. The body must echo `transactionId` or the BE answers 400.
   *
   *  `timeStamp` goes out as a full ISO instant: the entity field is a java.sql.Timestamp, not
   *  a LocalDate (unlike charge-balance's `createdAt`, which must be a bare YYYY-MM-DD).
   *  NOTE: `description` is still never assigned server-side — see putTransactionStatus(). */
  updateTransaction(
    t: Pick<BeTransactionResponse, 'transactionId' | 'currency' | 'type' | 'chargeType' | 'description'>,
    agencyId: string,
    patch: { amount: number; status: string; timeStamp: string },
  ): Observable<BeTransactionResponse> {
    return this.http.put<BeTransactionResponse>(`${this.base}/transactions/${t.transactionId}`, {
      transactionId: t.transactionId, agencyId, currency: t.currency,
      amount: patch.amount, status: patch.status, timeStamp: patch.timeStamp,
      type: t.type ?? 'CHARGE', chargeType: t.chargeType, description: t.description,
    });
  }

  approveTopUp(t: Pick<BeTransactionResponse, 'transactionId' | 'currency' | 'amount' | 'description'>, agencyId: string): Observable<BeTransactionResponse> {
    return this.putTransactionStatus(t, agencyId, 'COMPLETED');
  }

  rejectTopUp(t: Pick<BeTransactionResponse, 'transactionId' | 'currency' | 'amount' | 'description'>, agencyId: string, note: string): Observable<BeTransactionResponse> {
    return this.putTransactionStatus(t, agencyId, 'FAILED', note ? `Rejected: ${note}` : undefined);
  }

  getAgents(): Observable<BeAgentResponse[]> {
    return this.http.get<{ data?: BeAgentResponse[] }>(
      `${this.base}/agencies/agents?pageNumber=1&pageSize=100`
    ).pipe(map(r => r.data ?? []));
  }

  /** Same data as getAgents(), but for an arbitrary agency (e.g. a sub-agency) rather
   *  than the one resolved from the caller's own JWT — powers the Agencies page's
   *  per-sub-agency "Members" drill-in. */
  getAgentsByAgencyId(agencyId: string): Observable<BeAgentResponse[]> {
    return this.http.get<{ data?: BeAgentResponse[] }>(
      `${this.base}/agencies/${agencyId}/agents?pageNumber=1&pageSize=100`
    ).pipe(map(r => r.data ?? []));
  }

  getTransactions(agencyId: string, pageNumber = 1, pageSize = 50, currency?: string): Observable<BePaginatedTx> {
    const currencyParam = currency ? `&currency=${encodeURIComponent(currency)}` : '';
    return this.http.get<BePaginatedTx>(
      `${this.base}/agencies/${agencyId}/transactions?pageNumber=${pageNumber}&pageSize=${pageSize}${currencyParam}`
    );
  }

  getMargins(agencyId: string): Observable<BeMarginResponse[]> {
    return this.http.get<BeMarginResponse[]>(`${this.base}/agencies/${agencyId}/margins`);
  }

  updateMargin(agencyId: string, marginId: string, body: object): Observable<BeMarginResponse> {
    return this.http.patch<BeMarginResponse>(
      `${this.base}/agencies/${agencyId}/margins/${marginId}`,
      body
    );
  }

  /* ── Currency margins (feature: hotel-reservation-currency, README §6) — root agency only.
   * Same be-agency-service base URL as every other call in this file. Rates are EUR-anchored on
   * the wire in both directions; the page does the base-currency re-anchoring itself. ────── */

  /** One row per platform currency, margin resolved — including currencies with no margin row
   *  yet (percentage 0, sourceMarginId null), so the table can offer "Add margin" for them. */
  getCurrencyMargins(agencyId: string): Observable<BeCurrencyMarginResponse[]> {
    return this.http.get<BeCurrencyMarginResponse[]>(`${this.base}/agencies/${agencyId}/currency-margins/effective`);
  }

  /** Find-or-create: there is no separate POST, a first margin for a currency is a PATCH too. */
  updateCurrencyMargin(agencyId: string, currency: string, percentage: number, reason?: string): Observable<BeCurrencyMarginResponse> {
    return this.http.patch<BeCurrencyMarginResponse>(
      `${this.base}/agencies/${agencyId}/currency-margins/${currency}`,
      reason ? { percentage, reason } : { percentage },
    );
  }

  /** Freezes the currency at `ratePerEur` (EUR-anchored, whatever the network's base currency). */
  pinCurrencyRate(agencyId: string, currency: string, ratePerEur: number, reason?: string): Observable<BeCurrencyMarginResponse> {
    return this.http.post<BeCurrencyMarginResponse>(
      `${this.base}/agencies/${agencyId}/currency-margins/${currency}/pin`,
      reason ? { ratePerEur, reason } : { ratePerEur },
    );
  }

  /** Force-fetches this currency's market rate now, outside the daily 06:00 feed cycle
   *  (feature: hotel-currency-rate-refresh). Returns the row as it stands afterwards — a feed
   *  with no rate comes back 200 with `marketRateUnavailable`, not as an error, so the caller
   *  has to read the body rather than only the status. 409 = pinned, 429 = throttled. */
  refreshCurrencyRate(agencyId: string, currency: string): Observable<BeCurrencyMarginResponse> {
    return this.http.post<BeCurrencyMarginResponse>(
      `${this.base}/agencies/${agencyId}/currency-margins/${currency}/refresh-rate`, {});
  }

  /** Resumes daily auto-update for the currency. */
  unpinCurrencyRate(agencyId: string, currency: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/agencies/${agencyId}/currency-margins/${currency}/pin`);
  }

  /** The platform catalogue behind the Add Currency picker (feature:
   *  hotel-currencies-add-currency §2). Not under /agencies — it is platform-wide, not per-network. */
  getCurrencyCatalog(): Observable<BeCurrencyCatalogResponse[]> {
    return this.http.get<BeCurrencyCatalogResponse[]>(`${this.base}/currencies/catalog`);
  }

  /** Adds a currency to the network's quoted set. The entry is created neutral — no margin
   *  (`percentage` null / no source margin row) and auto-update on — and the response is the new
   *  row in the same shape as the effective list, so the table can insert it without refetching.
   *  409 when the network already quotes it. */
  addCurrencyMargin(agencyId: string, currency: string): Observable<BeCurrencyMarginResponse> {
    return this.http.post<BeCurrencyMarginResponse>(
      `${this.base}/agencies/${agencyId}/currency-margins`, { currency },
    );
  }

  getCurrencyMarginHistory(agencyId: string, currency: string): Observable<BeCurrencyMarginHistoryResponse[]> {
    return this.http.get<BeCurrencyMarginHistoryResponse[]>(`${this.base}/agencies/${agencyId}/currency-margins/${currency}/history`);
  }
}
